import { InitializeMode, PersistenceLevel, Store } from '@/core/flux.service';
import {
  AgentType,
  AnswerStyle,
  ChatMessage,
  FontSize,
  ReasoningStyle,
  SelectedDocument,
} from '@/aiAssistant/aiAssistant.types';
import _ from 'lodash';

interface AIAssistantState {
  displayed: boolean;
  maximized: boolean;
  messages: ChatMessage[];
  selectedAgent: AgentType;
  fontSize: FontSize;
  externalPrompt: { prompt: string; agent?: AgentType; insertOnly?: boolean } | undefined;
  answerStyle: AnswerStyle;
  reasoningStyle: ReasoningStyle;
  refreshHistory: boolean;
  activeChat: { id: string; name: string } | undefined;
  historySearchValue: string;
  selectedDocument: SelectedDocument | SelectedDocument[] | null;
  isProcessingExternalInput: 'running' | 'completed' | 'stopped';
}

export class AiAssistantStore extends Store {
  static readonly storeName = 'sqAiAssistantStore';
  persistenceLevel: PersistenceLevel = 'WORKBENCH';

  initialize(initializeMode: InitializeMode) {
    this.state = this.immutable({
      displayed: false,
      lastSubmittedPrompt: '',
      maximized: false,
      fontSize: 'small',
      answerStyle: 'moderate',
      reasoningStyle: 'enabled',
      refreshHistory: true,
      activeChat: undefined,
      historySearchValue: '',
      isProcessingExternalInput: 'stopped',
      selectedDocument: null,
    });
  }

  get displayed() {
    return this.state.get('displayed');
  }

  get maximized() {
    return this.state.get('maximized');
  }

  get messages() {
    return this.state.get('messages') || [];
  }

  get selectedAgent() {
    return this.state.get('selectedAgent');
  }

  get fontSize() {
    return this.state.get('fontSize') || 'small';
  }

  get externalPrompt() {
    return this.state.get('externalPrompt');
  }

  get answerStyle() {
    return this.state.get('answerStyle');
  }

  get reasoningStyle() {
    return this.state.get('reasoningStyle');
  }

  get refreshHistory() {
    return this.state.get('refreshHistory');
  }

  get activeChat() {
    return this.state.get('activeChat');
  }

  get historySearchValue() {
    return this.state.get('historySearchValue') || '';
  }

  get isProcessingExternalInput() {
    return this.state.get('isProcessingExternalInput');
  }

  get selectedDocument() {
    return this.state.get('selectedDocument');
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate() {
    return _.omit(this.state.serialize(), ['messages', 'externalPrompt', 'historySearchValue', 'selectedDocument']);
  }

  /**
   * Re-creates the AI Store. All necessary data needed to rehydrate is persisted so no actions have to be
   * called.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: AIAssistantState) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    /**
     * Sets the displayed flag
     */
    AI_ASSISTANT_DISPLAYED: (payload: { displayed: boolean }) => {
      this.state.set('displayed', payload.displayed);
    },

    /**
     * Sets the maximized flag
     */
    AI_ASSISTANT_MAXIMIZED: (payload: { maximized: boolean }) => {
      this.state.set('maximized', payload.maximized);
    },

    AI_ASSISTANT_SET_MESSAGES: (payload: { messages: ChatMessage[] }) => {
      this.state.set('messages', payload.messages);
    },

    AI_ASSISTANT_SET_SELECTED_AGENT: (payload: { agent: AgentType }) => {
      this.state.set('selectedAgent', payload.agent);
    },

    AI_ASSISTANT_SET_FONT_SIZE: (payload: { fontSize: FontSize }) => {
      this.state.set('fontSize', payload.fontSize);
    },

    AI_ASSISTANT_EXTERNAL_PROMPT: (
      payload: { prompt: string; agent?: AgentType; insertOnly?: boolean } | undefined,
    ) => {
      this.state.set('externalPrompt', payload);
    },

    AI_ASSISTANT_SET_ANSWER_STYLE: (payload: { answerStyle: AnswerStyle }) => {
      this.state.set('answerStyle', payload.answerStyle);
    },

    AI_ASSISTANT_SET_REASONING_STYLE: (payload: { reasoningStyle: ReasoningStyle }) => {
      this.state.set('reasoningStyle', payload.reasoningStyle);
    },

    AI_ASSISTANT_SET_REFRESH_HISTORY: (payload: { refreshHistory: boolean }) => {
      this.state.set('refreshHistory', payload.refreshHistory);
    },

    AI_ASSISTANT_SET_ACTIVE_CHAT: (payload: { chat: { id: string; name: string } | undefined }) => {
      this.state.set('activeChat', payload.chat);
    },

    AI_ASSISTANT_SET_HISTORY_SEARCH: (payload: { searchValue: string }) => {
      this.state.set('historySearchValue', payload.searchValue);
    },

    AI_ASSISTANT_SET_PROCESSING_EXTERNAL_INPUT: (payload: { value: 'running' | 'completed' | 'stopped' }) => {
      this.state.set('isProcessingExternalInput', payload.value);
    },

    AI_ASSISTANT_SET_SELECTED_DOCUMENT: (payload: { document: SelectedDocument | SelectedDocument[] | null }) => {
      this.state.set('selectedDocument', payload.document);
    },
  };
}
